/**
 * ThesisFitCard — Main display card for Thesis Fit
 *
 * Shows Thesis Score + Inverio Score side by side, Fit Gap badge,
 * fit label, auto-generated summary, and configure/edit CTA.
 * If no thesis configured, shows a setup CTA card.
 */

import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { Sliders, ArrowRight, Sparkles } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useThesisStore } from '@/lib/thesis/thesis-store';
import { computeThesisScore } from '@/lib/thesis/compute';
import { ARCHETYPE_INFO, FIT_LABELS_DISPLAY } from '@/lib/thesis/types';
import type { ThesisScore } from '@/lib/thesis/types';
import { FitGapBadge } from './FitGapBadge';

interface ThesisFitCardProps {
  dimensionScores: Record<string, number>;
  inverioScore: number;
  onConfigurePress: () => void;
}

export function ThesisFitCard({
  dimensionScores,
  inverioScore,
  onConfigurePress,
}: ThesisFitCardProps) {
  const colors = useThemeColors();
  const activeThesis = useThesisStore((s) => s.activeThesis);

  const thesisResult: ThesisScore | null = useMemo(() => {
    if (!activeThesis) return null;
    return computeThesisScore(dimensionScores, activeThesis.weights, inverioScore);
  }, [activeThesis, dimensionScores, inverioScore]);

  const handlePress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onConfigurePress();
  };

  // ── No thesis configured → Setup CTA ──
  if (!activeThesis || !thesisResult) {
    return (
      <Animated.View entering={FadeInDown.delay(100).duration(400)}>
        <AnimatedPressable onPress={handlePress}>
          <View
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.primary[200],
              borderRadius: 16,
              padding: 20,
              borderStyle: 'dashed',
            }}
          >
            <View className="flex-row items-center gap-3 mb-3">
              <View
                className="w-10 h-10 rounded-xl items-center justify-center"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Sliders size={20} color={colors.primary[600]} />
              </View>
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                  Set Up Your Thesis
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-xs mt-0.5">
                  See how startups align with your priorities
                </Text>
              </View>
              <ArrowRight size={18} color={colors.primary[500]} />
            </View>

            {/* Archetype preview chips */}
            <View className="flex-row flex-wrap gap-1.5">
              {ARCHETYPE_INFO.filter((a) => a.id !== 'custom').map((arch) => (
                <View
                  key={arch.id}
                  className="px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    {arch.name}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </AnimatedPressable>
      </Animated.View>
    );
  }

  // ── Thesis configured → Score display ──
  const archetypeLabel =
    ARCHETYPE_INFO.find((a) => a.id === activeThesis.archetype)?.name ??
    activeThesis.name;

  return (
    <Animated.View entering={FadeInDown.delay(100).duration(400)}>
      <View
        style={{
          backgroundColor: colors.surface.primary,
          borderRadius: 16,
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {/* Score comparison row */}
        <View className="p-3">
          <View className="flex-row items-center justify-between mb-4">
            {/* Thesis Score */}
            <View className="items-center flex-1">
              <Text
                style={{ color: colors.text.tertiary }}
                className="text-xs font-medium uppercase tracking-wider mb-1"
              >
                Thesis
              </Text>
              <View
                className="px-4 py-2 rounded-xl"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Text
                  style={{ color: colors.primary[700] }}
                  className="text-xl font-bold"
                >
                  {thesisResult.thesisScore}
                </Text>
              </View>
            </View>

            {/* Fit Gap Badge (center) */}
            <View className="items-center mx-3">
              <FitGapBadge
                fitGap={thesisResult.fitGap}
                fitLabel={thesisResult.fitLabel}
                size="lg"
              />
              <Text
                style={{ color: thesisResult.fitColor, fontSize: 10, marginTop: 2, fontWeight: '600' }}
              >
                {FIT_LABELS_DISPLAY[thesisResult.fitLabel]}
              </Text>
            </View>

            {/* Inverio Score */}
            <View className="items-center flex-1">
              <Text
                style={{ color: colors.text.tertiary }}
                className="text-xs font-medium uppercase tracking-wider mb-1"
              >
                Inverio
              </Text>
              <View
                className="px-4 py-2 rounded-xl"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <Text
                  style={{ color: colors.text.primary }}
                  className="text-xl font-bold"
                >
                  {thesisResult.inverioScore}
                </Text>
              </View>
            </View>
          </View>

          {/* Summary */}
          <Text
            style={{ color: colors.text.secondary }}
            className="text-xs leading-4"
            numberOfLines={2}
          >
            {thesisResult.summary}
          </Text>
        </View>

        {/* Footer with archetype + edit */}
        <View
          className="flex-row items-center justify-between px-3 py-2"
          style={{
            backgroundColor: colors.surface.secondary,
            borderTopWidth: 1,
            borderTopColor: colors.border.light,
          }}
        >
          <View className="flex-row items-center gap-2">
            <Sparkles size={12} color={colors.primary[500]} />
            <Text style={{ color: colors.text.tertiary }} className="text-xs">
              {archetypeLabel}
            </Text>
          </View>
          <AnimatedPressable onPress={handlePress}>
            <View className="flex-row items-center gap-1">
              <Sliders size={12} color={colors.primary[600]} />
              <Text style={{ color: colors.primary[600] }} className="text-xs font-semibold">
                Edit Weights
              </Text>
            </View>
          </AnimatedPressable>
        </View>
      </View>
    </Animated.View>
  );
}
